import { computed } from 'vue';
import { useDataStore } from '@/store/useDataStore';
import { useUserStore } from '@/store/useUserStore';
import { useAvatarUtilities } from './ui/useAvatarUtilities';

export function useSenderInfo() {
  const dataStore = useDataStore();
  const userStore = useUserStore();

  const userId = computed(() => userStore.userId);

  // Use the centralized avatar utilities composable
  const { findContactById, createAvatarPropsFromContact } = useAvatarUtilities(userId.value);

  function findContactByUserId(senderId: string) {
    return dataStore.contactsData?.contacts?.find(c => c.user_id === senderId);
  }

  function findContactByName(senderId: string) {
    return dataStore.contactsData?.contacts?.find(
      c => c.name === senderId || senderId.includes(c.name) || c.name.includes(senderId)
    );
  }

  function getCurrentUser() {
    return dataStore.contactsData?.contacts?.find(c => c.id === userId.value);
  }

  function handleCurrentUser(senderId: string): string | null {
    if (senderId !== userId.value) return null;
    const currentUser = getCurrentUser();
    return currentUser?.name || 'You';
  }

  function handleContactLookup(senderId: string): string | null {
    if (!dataStore.contactsData?.contacts) return 'Loading...';

    // Use centralized findContactById from useAvatarUtilities
    const contactById = findContactById(senderId);
    if (contactById) return contactById.name || null;

    const contactByUserId = findContactByUserId(senderId);
    if (contactByUserId) return contactByUserId.name || null;

    return null;
  }

  function handleNameString(senderId: string): string | null {
    if (typeof senderId !== 'string' || !senderId.includes(' ')) return null;

    const contactByName = findContactByName(senderId);
    return contactByName?.name || senderId;
  }

  function getSenderName(senderId: string): string {
    if (!senderId) return 'Unknown Sender';

    const currentUserName = handleCurrentUser(senderId);
    if (currentUserName !== null) return currentUserName;

    const contactName = handleContactLookup(senderId);
    if (contactName !== null) return contactName;

    const nameString = handleNameString(senderId);
    if (nameString !== null) return nameString;

    return 'Account Deactivated';
  }

  // Consolidated avatar property lookup using the centralized composable
  function getAvatarProperty(senderId: string, property: 'imageSrc' | 'avatarColor'): string {
    if (!senderId) return '';

    const contact = findContactById(senderId);
    const avatarProps = createAvatarPropsFromContact(contact);
    return avatarProps?.[property] || '';
  }

  // Simplified avatar methods using the centralized composable
  function getSenderAvatar(senderId: string): string {
    return getAvatarProperty(senderId, 'imageSrc');
  }

  function getSenderAvatarColor(senderId: string): string {
    return getAvatarProperty(senderId, 'avatarColor');
  }

  return {
    getSenderName,
    getSenderAvatar,
    getSenderAvatarColor,
  };
}
